import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit, FiTrash2, FiSearch, FiFilter, FiDownload, FiUpload, FiTrendingUp, FiTrendingDown } from 'react-icons/fi';

interface Crop {
  id: number;
  name: string;
  name_hindi: string;
}

const CropPriceTable: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMarket, setFilterMarket] = useState('all');
  const [filterCrop, setFilterCrop] = useState('all');
  const [rates, setRates] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Add Price Entry state
  const [crops, setCrops] = useState<Crop[]>([]);
  const [markets, setMarkets] = useState<string[]>([]);
  const [addForm, setAddForm] = useState({
    crop_id: '',
    market_name: '',
    date: new Date().toISOString().slice(0, 10), // yyyy-mm-dd
    min_price: '',
    max_price: '',
    avg_price: '',
  });
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState('');
  const [addSuccess, setAddSuccess] = useState('');

  // Fetch crops for dropdown
  useEffect(() => {
    const cacheBuster = new Date().getTime();
    fetch(`https://mandilive.in/api/crops?_cb=${cacheBuster}`)
      .then(res => res.json())
      .then(data => setCrops(Array.isArray(data.data) ? data.data : []));
  }, []);

  // Update markets from rates
  useEffect(() => {
    setMarkets(Array.from(new Set(rates.map(r => r.market_name))));
  }, [rates]);

  // Prefill if entry exists for crop/market/date
  useEffect(() => {
    if (!addForm.crop_id || !addForm.market_name || !addForm.date) return;
    const existing = rates.find(r => String(r.crop_id) === String(addForm.crop_id) && r.market_name === addForm.market_name && (r.date === addForm.date || r.date === toHindiDate(addForm.date)));
    if (existing) {
      setAddForm(f => ({
        ...f,
        min_price: existing.min_price,
        max_price: existing.max_price,
        avg_price: existing.avg_price,
      }));
    } else {
      setAddForm(f => ({ ...f, min_price: '', max_price: '', avg_price: '' }));
    }
    // eslint-disable-next-line
  }, [addForm.crop_id, addForm.market_name, addForm.date, rates]);

  function toHindiDate(dateStr: string) {
    const d = new Date(dateStr);
    return d.toLocaleDateString('hi-IN', { day: 'numeric', month: 'long', year: 'numeric' });
  }

  const handleAddFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setAddForm(f => ({ ...f, [name]: value }));
  };

  // Add Price Entry handler
  const handleAddPrice = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddError('');
    setAddSuccess('');
    setAddLoading(true);
    try {
      // Check if entry exists
      const existing = rates.find(r => String(r.crop_id) === String(addForm.crop_id) && r.market_name === addForm.market_name && (r.date === addForm.date || r.date === toHindiDate(addForm.date)));
      // Convert date to Hindi format for API
      const hindiDate = toHindiDate(addForm.date);
      const isoDate = addForm.date;
      const payload = {
        crop_id: Number(addForm.crop_id),
        market_name: addForm.market_name,
        date: hindiDate,
        date_iso: isoDate,
        min_price: Number(addForm.min_price),
        max_price: Number(addForm.max_price),
        avg_price: Number(addForm.avg_price),
      };
      const url = 'https://mandilive.in/api/endpoints/mandi_rates.php';
      const method = existing ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setAddSuccess(existing ? 'Price entry updated.' : 'Price entry added.');
        setAddError('');
        // Refresh rates
        const r = await fetch(url);
        const d = await r.json();
        setRates(Array.isArray(d.data) ? d.data : []);
      } else {
        let err;
        try {
          err = await res.json();
        } catch (e) {
          err = { message: res.statusText };
        }
        console.error('API error:', res.status, err);
        if (res.status === 409 && method === 'POST') {
          setAddError('A price entry already exists for this crop, market, and date.');
        } else if (res.status === 404 && method === 'PUT') {
          setAddError('No matching record found for update. Please check crop, market, and date.');
        } else {
          setAddError(err.message || 'Failed to save price entry');
        }
      }
    } catch (err: any) {
      setAddError(err?.message || 'Failed to save price entry');
    }
    setAddLoading(false);
  };

// Fetch mandi_rates from API on mount
  React.useEffect(() => {
    const fetchRates = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await fetch('https://mandilive.in/api/endpoints/mandi_rates.php');
        const data = await res.json();
        setRates(Array.isArray(data.data) ? data.data : []);
      } catch (err: any) {
        setError('Failed to fetch mandi rates: ' + (err?.message || err));
        setRates([]);
      }
      setLoading(false);
    };
    fetchRates();
  }, []);

  // Get today's date in Hindi format (to match API)
  const today = new Date();
  const todayHindi = today.toLocaleDateString('hi-IN', { day: 'numeric', month: 'long', year: 'numeric' });

  // Remove old const crops/markets if present below (using state now)
  // Get all unique crop/market combos
  const combos = Array.from(
    new Set(rates.map(r => `${r.crop_id}|${r.market_name}`))
  ).map(key => {
    const [crop_id, market_name] = key.split('|');
    return { crop_id, market_name };
  });

  // For each combo, get today's and last available rate
  const tableRows = combos.map(({ crop_id, market_name }) => {
    const allRates = rates.filter(r => r.crop_id == crop_id && r.market_name === market_name);
    const todayRate = allRates.find(r => r.date === todayHindi);
    let lastRate = null;
    if (!todayRate) {
      // Find the latest available (by created_at or date order)
      lastRate = allRates.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0] || null;
    }
    return {
      crop_id,
      market_name,
      todayRate,
      lastRate,
      crop_name: (todayRate || lastRate)?.crop_name || '',
      crop_name_hindi: (todayRate || lastRate)?.crop_name_hindi || '',
    };
  });

  // Filtering (search, crop, market)
  const filteredRows = tableRows.filter(row => {
    const matchesSearch = (row.crop_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (row.crop_name_hindi || '').includes(searchTerm) ||
      (row.market_name || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesMarket = filterMarket === 'all' || row.market_name === filterMarket;
    const matchesCrop = filterCrop === 'all' || row.crop_name === filterCrop;
    return matchesSearch && matchesMarket && matchesCrop;
  });



  return (
    <div className="space-y-6">
      {/* Add Price Entry Form */}
      <div className="bg-white rounded-lg shadow p-6 border border-gray-200 mb-6">
        <h3 className="text-lg font-semibold mb-3 flex items-center">
          <FiPlus className="mr-2" /> Add Price Entry
        </h3>
        <form className="grid grid-cols-1 md:grid-cols-6 gap-4 items-end" onSubmit={handleAddPrice} >
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Crop</label>
            <select
              name="crop_id"
              value={addForm.crop_id}
              onChange={handleAddFormChange}
              className="w-full border rounded px-2 py-1"
              required
            >
              <option value="">Select Crop</option>
              {crops.map(crop => (
                <option key={crop.id} value={crop.id}>{crop.name} ({crop.name_hindi})</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Market</label>
            <select
              name="market_name"
              value={addForm.market_name}
              onChange={handleAddFormChange}
              className="w-full border rounded px-2 py-1"
              required
            >
              <option value="">Select Market</option>
              {markets.map(mkt => (
                <option key={mkt} value={mkt}>{mkt}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Date</label>
            <input
              type="date"
              name="date"
              value={addForm.date}
              onChange={handleAddFormChange}
              className="w-full border rounded px-2 py-1"
              required
              max={new Date().toISOString().slice(0, 10)}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Min Price</label>
            <input
              type="number"
              name="min_price"
              value={addForm.min_price}
              onChange={handleAddFormChange}
              className="w-full border rounded px-2 py-1"
              required
              min={0}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Max Price</label>
            <input
              type="number"
              name="max_price"
              value={addForm.max_price}
              onChange={handleAddFormChange}
              className="w-full border rounded px-2 py-1"
              required
              min={0}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Avg Price</label>
            <input
              type="number"
              name="avg_price"
              value={addForm.avg_price}
              onChange={handleAddFormChange}
              className="w-full border rounded px-2 py-1"
              required
              min={0}
            />
          </div>
          <div className="md:col-span-6 flex items-center space-x-3 mt-2">
            <button
              type="submit"
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-300"
              disabled={addLoading}
            >
              {addLoading ? 'Saving...' : (rates.find(r => String(r.crop_id) === String(addForm.crop_id) && r.market_name === addForm.market_name && (r.date === addForm.date || r.date === toHindiDate(addForm.date))) ? 'Update' : 'Add')}
            </button>
            {addSuccess && <span className="text-green-600 text-sm">{addSuccess}</span>}
            {addError && <span className="text-red-600 text-sm">{addError}</span>}
          </div>
        </form>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Crop Price Table</h1>
          <p className="text-gray-600 mt-1">Manage mandi rates and crop pricing data</p>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-3">
          
          <button className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
            <FiPlus className="mr-2 h-4 w-4" />
            Add Price Entry
          </button>
        </div>
      </div>

     

     
      {/* Price Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Today Prices</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Crop
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Market
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Min Price (₹)
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Max Price (₹)
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Avg Price (₹)
                </th>
                
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
              
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredRows.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center py-8 text-gray-500">No price data found</td>
                </tr>
              )}
              {filteredRows.map((row, idx) => {
                const rate = row.todayRate || row.lastRate;
                return (
                  <tr key={row.crop_id + '-' + row.market_name} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{row.crop_name}</div>
                        <div className="text-sm text-gray-500">{row.crop_name_hindi}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {row.market_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {row.todayRate ? `₹${row.todayRate.min_price}` : 'NA'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {row.todayRate ? `₹${row.todayRate.max_price}` : 'NA'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {row.todayRate ? `₹${row.todayRate.avg_price}` : 'NA'}
                    </td>
                   
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {row.todayRate ? row.todayRate.date : 'NA'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                     
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Empty State */}
      {filteredRows.length === 0 && (
        <div className="text-center py-12">
          <FiTrendingUp className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No price data found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm || filterMarket !== 'all' || filterCrop !== 'all'
              ? 'Try adjusting your search or filter criteria.'
              : 'Get started by adding your first price entry.'
            }
          </p>
        </div>
      )}
      {/* Last Available Prices Section */}
      <div className="mt-12">
        <h3 className="text-lg font-semibold mb-4">Last Available Prices</h3>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Crop</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Market</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Min Price (₹)</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Max Price (₹)</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Avg Price (₹)</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredRows
                  .map(row => {
                    if (row.todayRate) return null;
                    // Find the most recent rate before today
                    const allRates = rates.filter(r => r.crop_id == row.crop_id && r.market_name === row.market_name);
                    // Parse today's date in both Hindi and ISO for comparison
                    const todayDate = new Date('2025-08-02');
                    // Find all rates before today
                    const beforeTodayRates = allRates.filter(r => {
                      // Try to parse r.date as ISO or fallback to created_at
                      let rateDate: Date | null = null;
                      // Try parsing as YYYY-MM-DD
                      if (/\d{4}-\d{2}-\d{2}/.test(r.date)) {
                        rateDate = new Date(r.date);
                      } else {
                        // Try created_at
                        rateDate = r.created_at ? new Date(r.created_at) : null;
                      }
                      return rateDate && rateDate < todayDate;
                    });
                    if (beforeTodayRates.length === 0) return null;
                    // Get the most recent
                    const last = beforeTodayRates.sort((a, b) => {
                      const aDate = new Date(a.date || a.created_at);
                      const bDate = new Date(b.date || b.created_at);
                      return bDate.getTime() - aDate.getTime();
                    })[0];
                    return (
                      <tr key={row.crop_id + '-' + row.market_name + '-last'} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{row.crop_name}</div>
                            <div className="text-sm text-gray-500">{row.crop_name_hindi}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.market_name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">₹{last.min_price}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">₹{last.max_price}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">₹{last.avg_price}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{last.date}</td>
                      </tr>
                    );
                  })}
                {filteredRows.filter(row => !row.todayRate && !row.lastRate).length === 0 ? null : (
                  <tr><td colSpan={6} className="text-center py-6 text-gray-400">No last available prices missing today.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CropPriceTable;
